import { Circle } from "@mui/icons-material";
import { Card, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { memo, useMemo } from "react";

import { generatePastelColor } from "../../utils/color";

interface IssueData {
  issue: string;
  count: number;
  color?: string;
}

interface SupportData {
  waiting_support?: number;
  support_inprogress?: number;
  support_group_by_issue?: IssueData[];
}

interface DashboardData {
  today?: SupportData;
  today_qty?: SupportData;
  all_time?: SupportData;
  all_time_qty?: SupportData;
  tomorrow?: SupportData;
  tomorrow_qty?: SupportData;
}

interface ComponentProps {
  isTask: boolean;
  titleOverview: string;
  data: DashboardData;
}

// Shared card style
const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "4px",
  padding: "16px",
};

// Helper functions for safe data access
const getSupportCount = (
  data: SupportData | undefined,
  field: keyof SupportData,
): number => {
  return (data?.[field] as number) || 0;
};

// Stat card component to reduce duplication
interface StatCardProps {
  label: string;
  value: number;
  textColor?: string;
  valueColor?: string;
}

const StatCard = memo(
  ({
    label,
    value,
    textColor = "text-gray-400",
    valueColor = "",
  }: StatCardProps) => (
    <div className="col-span-1" style={cardStyle}>
      <div className="flex flex-col gap-1">
        <div className={`text-sm font-bold ${textColor}`}>{label}</div>
        <div className={`text-3xl font-bold ${valueColor}`}>{value}</div>
      </div>
    </div>
  ),
);

StatCard.displayName = "StatCard";

const Support = memo(({ isTask, titleOverview, data }: ComponentProps) => {
  const dataGroupsIssues = useMemo(() => {
    const allTimeData = isTask ? data?.all_time : data?.all_time_qty;
    const issues = allTimeData?.support_group_by_issue || [];

    if (!issues.length) return [];

    return issues.map((issue) => ({
      ...issue,
      color: generatePastelColor(),
    }));
  }, [isTask, data?.all_time, data?.all_time_qty]);

  const calculatedData = useMemo(() => {
    const todayData = isTask ? data?.today : data?.today_qty;
    const allTimeData = isTask ? data?.all_time : data?.all_time_qty;
    const tomorrowData = isTask ? data?.tomorrow : data?.tomorrow_qty;

    // Today's counts
    const todaySupport = getSupportCount(todayData, "waiting_support");
    const todayInProgress = getSupportCount(todayData, "support_inprogress");

    // Tomorrow's counts
    const tomorrowSupport = getSupportCount(tomorrowData, "waiting_support");
    const tomorrowInProgress = getSupportCount(
      tomorrowData,
      "support_inprogress",
    );

    // Yesterday's counts (all_time - today - tomorrow)
    const yesterdaySupport = Math.max(
      0,
      getSupportCount(allTimeData, "waiting_support") -
        todaySupport -
        tomorrowSupport,
    );
    const yesterdayInProgress = Math.max(
      0,
      getSupportCount(allTimeData, "support_inprogress") -
        todayInProgress -
        tomorrowInProgress,
    );

    return {
      todaySupport,
      todayInProgress,
      yesterdaySupport,
      yesterdayInProgress,
    };
  }, [isTask, data]);

  const totalIssueCount = useMemo(() => {
    return dataGroupsIssues.reduce((acc, issue) => acc + issue.count, 0);
  }, [dataGroupsIssues]);

  const pieChartData = useMemo(() => {
    return dataGroupsIssues.map((issue) => ({
      id: issue.issue,
      value: issue.count,
    }));
  }, [dataGroupsIssues]);

  const pieChartColors = useMemo(() => {
    return dataGroupsIssues.map((issue) => issue.color);
  }, [dataGroupsIssues]);

  return (
    <Card className="p-4 mb-4">
      <Typography variant="body1" className="font-semibold mb-2">
        Support
      </Typography>

      <div className="relative flex items-center justify-center mb-2">
        <PieChart
          colors={pieChartColors}
          series={[
            {
              data: pieChartData,
              outerRadius: 80,
              innerRadius: 65,
            },
          ]}
          width={178}
          height={178}
          margin={{ right: 4 }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Typography variant="h5" className="font-bold">
            {totalIssueCount}
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            {titleOverview}
          </Typography>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        {dataGroupsIssues.map((issue, index) => (
          <div
            className="col-span-1 flex gap-2 items-center"
            key={`issue-detail-${issue.issue}-${index}`}
          >
            <Circle style={{ color: issue.color }} />
            <Typography variant="body2" className="text-sm">
              {issue.issue}: {issue.count}
            </Typography>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard label="SUPPORT TODAY" value={calculatedData.todaySupport} />
        <StatCard
          label="SUPPORT YESTERDAY"
          value={calculatedData.yesterdaySupport}
          textColor="text-error"
          valueColor="text-error-dark"
        />
        <StatCard
          label="IN PROGRESS TODAY"
          value={calculatedData.todayInProgress}
        />
        <StatCard
          label="IN PROGRESS YESTERDAY"
          value={calculatedData.yesterdayInProgress}
          textColor="text-error"
          valueColor="text-error-dark"
        />
      </div>
    </Card>
  );
});

Support.displayName = "Support";

export default Support;
